import { FormattedDate, FormattedMessage } from "react-intl";

import { Empty, Tag, Timeline, Typography } from "antd";

import { CmtSectionCard } from "@app/components/common/CmtSectionCard.tsx";
import { User } from "@app/models/User.ts";
import { colors } from "@app/theme.ts";

// SAP stores open-ended contracts with a far-future end date (9999-12-31).
const isOpenEnded = (endDate: Date) => new Date(endDate).getFullYear() >= 9000;

const isCurrent = ({ startDate, endDate }: User["contractPeriods"][number]) => {
  const now = Date.now();
  return (
    new Date(startDate).getTime() <= now && new Date(endDate).getTime() >= now
  );
};

interface CmtUserContractPeriodsProps {
  user: User;
}

export const CmtUserContractPeriods = ({
  user,
}: CmtUserContractPeriodsProps) => {
  const periods = [...user.contractPeriods].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
  );

  return (
    <CmtSectionCard
      title={
        <FormattedMessage
          id="collaborator.contract_period"
          defaultMessage="Contract Periods"
        />
      }
    >
      {periods.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Timeline
          style={{ marginTop: "0.5rem" }}
          items={periods.map((period, index) => {
            const current = isCurrent(period);
            return {
              key: `${String(period.startDate)}-${index}`,
              color: current ? colors.pink : colors.grey2,
              children: (
                <>
                  <Typography.Text strong>
                    <FormattedMessage
                      id="collaborator.contract"
                      defaultMessage="Contract"
                    />{" "}
                    {periods.length - index}
                  </Typography.Text>
                  {current && (
                    <Tag color="magenta" style={{ marginLeft: "0.5rem" }}>
                      <FormattedMessage
                        id="collaborator.contract.current"
                        defaultMessage="Current"
                      />
                    </Tag>
                  )}
                  <div>
                    <Typography.Text type="secondary">
                      <FormattedDate
                        value={new Date(period.startDate)}
                        dateStyle="medium"
                      />
                      {" → "}
                      {isOpenEnded(period.endDate) ? (
                        <FormattedMessage
                          id="collaborator.contract.open_ended"
                          defaultMessage="Today"
                        />
                      ) : (
                        <FormattedDate
                          value={new Date(period.endDate)}
                          dateStyle="medium"
                        />
                      )}
                    </Typography.Text>
                  </div>
                </>
              ),
            };
          })}
        />
      )}
    </CmtSectionCard>
  );
};
